"use client";

import { InfoCircleOutlined } from "@ant-design/icons";
import { Button, Flex } from "antd";
import { useCallback, useId, useMemo, useState } from "react";
import { SalesSKUInvoice } from "tm-shared/types";
import { formatNumber, formatUAHNarrow } from "tm-shared/utils";

import BottleIcon from "@/assets/icons/bottle.svg?react";
import DropIcon from "@/assets/icons/drop.svg?react";
import PriceTagIcon from "@/assets/icons/price-tag.svg?react";
import { useCatalog } from "@/domains/product";
import { useAppSelector } from "@/hooks/redux";
import { NestedAccordion, PrimaryText, SecondaryText } from "@/shared/ui";

import { EmptySalesResult } from "../EmptySalesResult";
import { InvoiceSKUInfoModal } from "./InvoiceSKUInfoModal";
import { StyledNestedAccordionWrapper, StyledSKURaw } from "./Styled";

export const ICONS = {
    bottle: <BottleIcon />,
    litre: <DropIcon />,
    money: <PriceTagIcon />,
} as const;

const Icon = ({ type }: { type: "bottle" | "litre" | "money" }) => {
    const icon = ICONS[type];

    return <SecondaryText fontSize={22}>{icon}</SecondaryText>;
};

export const BySKU = () => {
    const { products } = useCatalog();
    const skuSales = useAppSelector((state) => state.work.skuSales);
    const invoiceSKUInfoModalKey = useId();
    const [modalInfo, setModalInfo] = useState<SalesSKUInvoice | null>(null);

    const handleOpenModal = (invoice: SalesSKUInvoice) => {
        setModalInfo(invoice);
    };

    const handleCloseModal = useCallback(() => {
        setModalInfo(null);
    }, []);

    const accordionItems = useMemo(() => {
        return skuSales
            .map((skuItem) => {
                const currentProduct = products[skuItem.barcode];

                if (!currentProduct) {
                    return null;
                }
                const currentProductCapacity =
                    +currentProduct?.params?.capacity?.value;
                const totalSum = Object.values(skuItem.invoices).reduce(
                    (sum, inv) => sum + inv.countSKU * inv.price,
                    0,
                );
                const totalB = Object.values(skuItem.invoices).reduce(
                    (sum, inv) => sum + (inv.countSKU || 0),
                    0,
                );
                const totalL = (totalB * currentProductCapacity).toFixed(1);

                return {
                    key: skuItem.barcode,
                    label: (
                        <Flex
                            gap={16}
                            align="flex-start"
                            justify="space-between"
                        >
                            <PrimaryText fontWeight="400" fontSize={14}>
                                {currentProduct?.name}
                            </PrimaryText>

                            <Flex vertical align="flex-end" gap={4}>
                                <PrimaryText textColor="accent" textNoWrap>
                                    {formatUAHNarrow(totalSum)}
                                </PrimaryText>

                                <Flex gap={6} align="center">
                                    <Flex align="center">
                                        <PrimaryText fontSize={12} textNoWrap>
                                            {totalB}
                                        </PrimaryText>

                                        <Icon type="bottle" />
                                    </Flex>

                                    <Flex align="center">
                                        <PrimaryText fontSize={12} textNoWrap>
                                            {totalL}
                                        </PrimaryText>

                                        <Icon type="litre" />
                                    </Flex>
                                </Flex>
                            </Flex>
                        </Flex>
                    ),
                    children: (
                        <Flex vertical style={{ paddingLeft: 24 }} gap={12}>
                            {Object.values(skuItem.invoices).map((invoice) => {
                                const values = [
                                    {
                                        id: 1,
                                        value: formatUAHNarrow(invoice.price),
                                        icon: <Icon type="money" />,
                                    },
                                    {
                                        id: 2,
                                        value: invoice.countSKU,
                                        icon: <Icon type="bottle" />,
                                    },
                                    {
                                        id: 3,
                                        value: formatNumber(
                                            invoice.countSKU *
                                                currentProductCapacity,
                                            1,
                                        ),
                                        icon: <Icon type="litre" />,
                                    },
                                ];

                                return (
                                    <StyledSKURaw key={invoice.invoiceId}>
                                        <Flex justify="space-between" gap={4}>
                                            <Flex gap={4}>
                                                <Button
                                                    onClick={() =>
                                                        handleOpenModal(invoice)
                                                    }
                                                    icon={
                                                        <InfoCircleOutlined />
                                                    }
                                                    type="text"
                                                />

                                                <PrimaryText fontSize={14}>
                                                    {invoice.invoiceId}
                                                </PrimaryText>
                                            </Flex>

                                            <PrimaryText
                                                textColor="accent"
                                                textNoWrap
                                            >
                                                {formatUAHNarrow(
                                                    invoice.countSKU *
                                                        invoice.price,
                                                )}
                                            </PrimaryText>
                                        </Flex>

                                        <Flex justify="space-between" gap={16}>
                                            {values.map(
                                                ({ id, icon, value }) => (
                                                    <Flex
                                                        key={id}
                                                        align="center"
                                                        gap={2}
                                                    >
                                                        <PrimaryText
                                                            fontSize={12}
                                                        >
                                                            {value}
                                                        </PrimaryText>

                                                        {icon}
                                                    </Flex>
                                                ),
                                            )}
                                        </Flex>
                                    </StyledSKURaw>
                                );
                            })}
                        </Flex>
                    ),
                };
            })
            .filter((item): item is NonNullable<typeof item> => item !== null);
    }, [products, skuSales]);

    if (!skuSales.length) return <EmptySalesResult />;

    return (
        <>
            {modalInfo && (
                <InvoiceSKUInfoModal
                    open
                    modalKey={invoiceSKUInfoModalKey}
                    onClose={handleCloseModal}
                    invoice={modalInfo}
                />
            )}

            <StyledNestedAccordionWrapper>
                <NestedAccordion accordion level={0} items={accordionItems} />
            </StyledNestedAccordionWrapper>
        </>
    );
};
